"use client";

import { Compass, Box, Leaf, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function Pillars() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    index: "PICE",
    icon: Compass,
    title: "Il Progetto",
    italicWord: "",
    titleSuffix: "",
    description: "The Room conversations",
    ctaLabel: "Scopri →",
    link: "/LioniaProject"
  }, {
    index: "§ 02.2",
    icon: Box,
    title: "I Servizi",
    italicWord: "",
    titleSuffix: "",
    description: "Audit, restyle services",
    ctaLabel: "Scopri →",
    link: "/ServicesPage"
  }, {
    index: "§ 02.3",
    icon: Leaf,
    title: "Il Blog",
    italicWord: "",
    titleSuffix: "",
    description: "Blog series: L'elefante nella stanza, Butterfly Impact, Trojan Moves",
    ctaLabel: "Scopri →",
    link: "/BlogPage"
  }];
  return <section data-section-id="2110" id="core-project-pillars" className="relative overflow-hidden bg-[#0A0A0A] py-28 md:py-36 text-white">
      <div aria-hidden="true" className="pointer-events-none absolute right-[-4%] top-[2%] hidden select-none md:block" style={{
      fontFamily: "Outfit, sans-serif",
      fontSize: "clamp(320px, 52vw, 760px)",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px #FFFFFF",
      opacity: 0.06,
      fontWeight: 700
    }}>
        02
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-[-8%] top-[1%] block select-none md:hidden" style={{
      fontFamily: "Outfit, sans-serif",
      fontSize: "320px",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px #FFFFFF",
      opacity: 0.06,
      fontWeight: 700
    }}>
        02
      </div>

      <div className="relative mx-auto max-w-[1200px] px-4 md:px-6 lg:px-10">
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            

            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-[#2A2A2A]" />
              <div className="flex items-center gap-1">
                <span className="h-1 w-1 rounded-lg bg-[#F5C518]" />
                <span className="h-1 w-1 rounded-lg bg-[#F5C518]" />
                <span className="h-1 w-1 rounded-lg bg-[#F5C518]" />
              </div>
            </div>

            <h2 className="mt-8 font-default" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.02,
            color: "#FFFFFF"
          }}>
              Un progetto, tre anime.
            </h2>
          </div>

          <div className="lg:col-span-5 lg:flex lg:items-end">
            <p className="font-default max-w-md text-gray-400" style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.6
          }}>
              MARINELA approaches every project through three core disciplines — a shared vocabulary of excellence that ensures every brand, design, and strategy speaks with clarity and purpose.
            </p>
          </div>
        </div>

        <div ref={cardsRef} className="mt-20 grid grid-cols-1 gap-4 md:grid-cols-3 lg:gap-6">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <Link key={i} to={pillar.link} data-index={i} className={`transition-all ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDuration: "720ms",
            transitionDelay: `${i * 80}ms`
          }}>
                <Card className="group relative h-full overflow-hidden p-5 transition-transform duration-300 ease-out hover:-translate-y-0 bg-[#0A0A0A] border border-[#F5C518] shadow-md" style={{
              borderRadius: "16px"
            }}>
                  <div className="flex items-center gap-2 uppercase" style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.875rem",
                fontWeight: 500,
                letterSpacing: "0.2em",
                lineHeight: 1
              }}>
                    
                    <span className="text-[#2A2A2A]">—</span>
                    
                  </div>

                  <div className="mt-6 transition-transform duration-[400ms] ease-out group-hover:rotate-6">
                    <Icon size={28} strokeWidth={1.5} className="text-[#F5C518]" />
                  </div>

                  <h3 className="mt-5 font-default" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 1.875rem)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
                color: "#FFFFFF"
              }}>
                    {pillar.title}{" "}
                    {pillar.italicWord && <em className="font-default" style={{
                  fontStyle: "italic",
                  fontWeight: 600
                }}>
                        {pillar.italicWord}
                      </em>}
                  </h3>

                  <p className="font-default mt-3 text-gray-400" style={{
                fontSize: "1rem",
                fontWeight: 400,
                lineHeight: 1.5
              }}>
                    {pillar.description}
                  </p>

                  <div className="mt-6 flex items-center gap-3">
                    <div className="h-px flex-1 bg-[#2A2A2A]" />
                    <div className="flex items-center gap-1">
                      <span className="h-1 w-1 rounded-lg bg-[#F5C518]" />
                      <span className="h-1 w-1 rounded-lg bg-[#F5C518]" />
                      <span className="h-1 w-1 rounded-lg bg-[#F5C518]" />
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="group/link relative inline-flex items-center gap-2 font-default" style={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "#F5C518"
                }}>
                      <span className="relative">
                        {pillar.ctaLabel}
                        <span className="absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-300 ease-out group-hover/link:w-full bg-[#F5C518]" />
                      </span>
                      <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                    </div>
                  </div>
                </Card>
              </Link>;
        })}
        </div>
      </div>
    </section>;
}